import { Button } from '@/components/common/button'
import { Card } from '@/components/common/card'
import { NetworkImage } from '@/components/common/networkImage'
import { ProtocolImage } from '@/components/common/protocolImage'
import { RoundImage } from '@/components/common/roundImage'
import { SearchIcon } from '@/components/icons/search'
import { routes } from '@/config/routes'
import { useMixpanel } from '@/context/mixpanel'
import { FIXED_TERM_MARKETS_PER_PAGE } from '@/hooks/useFixedTermMarketsList'
import { cn } from '@/utils/cn'
import { formatTimeToMaturity } from '@/utils/fixedTermMarkets/formatTimeToMaturity'
import type { FixedTermMarket } from '@/utils/internalAppSdk'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { getValueColor } from '@/utils/styles/getValueColor'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatDecimalToPercentage, formatUSD, getTokenImage } from '@vaultsfyi/common/utils'
import { Link, generatePath } from 'react-router'
import { ListPagination } from '../common/listPagination'

interface FixedTermMarketsCardsProps {
  markets: FixedTermMarket[]
  currentPage: number
  itemsCount: number
  onPageChange: (page: number) => void
  className?: string
}

export const FixedTermMarketsCards = ({
  markets,
  currentPage,
  itemsCount,
  onPageChange,
  className,
}: FixedTermMarketsCardsProps) => {
  const { trackEvent } = useMixpanel()
  const currentTimestamp = Date.now() / SECOND_IN_MILLISECONDS

  if (markets.length === 0) {
    return (
      <Card className={cn('flex w-full flex-col items-center justify-center gap-4 py-4', className)}>
        <SearchIcon size={48} />
        <p className="font-bold text-xl">No results for selected filters</p>
      </Card>
    )
  }

  return (
    <section className={cn('flex w-full flex-col gap-3', className)}>
      <div className="flex flex-col gap-2">
        {markets.map((market) => (
          <Card
            key={`${market.network.name}:${market.vaultId}`}
            as="article"
            className={cn('group relative flex flex-col gap-3 px-4 py-3', 'ring-inset has-[:focus-visible]:ring')}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <RoundImage
                  src={market.accountingAsset.assetLogo ?? getTokenImage(market.accountingAsset.symbol ?? 'fallback')}
                  fallback={getTokenImage('fallback')}
                  alt={market.accountingAsset.symbol ?? market.name}
                  className="size-8 shrink-0 p-1"
                />
                <div className="min-w-0">
                  <Button
                    variant="default"
                    size="default"
                    className={cn(
                      '!ring-0 block min-w-0 max-w-full text-heading-foreground',
                      'before:absolute before:inset-0 before:h-full before:w-full'
                    )}
                    asChild
                  >
                    <Link
                      to={generatePath(routes.fixedTermMarket, {
                        network: market.network.name,
                        vaultId: market.vaultId,
                      })}
                      prefetch="viewport"
                      onClick={() =>
                        trackEvent('Fixed term market card clicked', {
                          network: market.network.name,
                          vaultId: market.vaultId,
                        })
                      }
                    >
                      <h3 className="truncate font-medium text-sm" title={market.name}>
                        <span className="sr-only">Go to </span>
                        {market.name}
                        <span className="sr-only"> fixed term market page</span>
                      </h3>
                    </Link>
                  </Button>
                  <div className="flex items-center gap-1 text-xs">
                    <NetworkImage network={market.network.name} className="size-4 shrink-0" />
                    <span className="truncate capitalize">{getNetworkName(market.network.name)}</span>
                  </div>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <ProtocolImage protocol={market.underlyingProtocol} className="size-7 shrink-0 p-1" />
                <span className="truncate text-xs">{getProtocolDisplayName(market.underlyingProtocol)}</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Metric label="Time to maturity" value={formatTimeToMaturity(market.expiry, currentTimestamp)} />
              <Metric label="TVL" value={formatUSD(market.tvl.usd)} className="items-end text-right" />
              <Metric
                label="Fixed APY"
                value={formatDecimalToPercentage(market.fixedApy)}
                className="items-end text-right"
                valueClassName={getValueColor(market.fixedApy)}
              />
            </div>
          </Card>
        ))}
      </div>
      <ListPagination
        currentPage={currentPage}
        itemsCount={itemsCount}
        itemsPerPage={FIXED_TERM_MARKETS_PER_PAGE}
        onPageChange={onPageChange}
      />
    </section>
  )
}

interface MetricProps {
  label: string
  value: string
  className?: string
  valueClassName?: string
}

const Metric = ({ label, value, className, valueClassName }: MetricProps) => (
  <div className={cn('flex min-w-0 flex-col gap-0.5', className)}>
    <span className="truncate text-body-foreground text-xs">{label}</span>
    <span className={cn('truncate font-medium text-heading-foreground text-sm tabular-nums', valueClassName)}>
      {value}
    </span>
  </div>
)
